/* ui-dashboard.js — renders the two dashboard widgets from cad.files only.
   No network call happens on render; background syncs update the data and
   the widgets re-render when a sync completes. */
(function () {
  'use strict';
  const CAD = (window.CAD = window.CAD || {});

  const RANGE_OPTS = [
    { value: '7', label: 'Last 7 days' },
    { value: '30', label: 'Last 30 days' },
    { value: '90', label: 'Last 90 days' },
    { value: 'all', label: 'All synced data' },
    { value: 'custom', label: 'Custom range' },
  ];

  const SLIDER_STOPS = [
    { label: 'High', include: ['high'] },
    { label: '+ Medium', include: ['high', 'medium'] },
    { label: '+ Low', include: ['high', 'medium', 'low'] },
    { label: 'All', include: ['high', 'medium', 'low'] },
  ];

  const state = {
    rangeMode: '30',
    customStart: null,
    customEnd: null,
    sliderStop: 3,
    w1Mode: 'list',
    bannerDismissed: false,
  };

  // ---------- shared range helpers (also used by the drill-down) ----------

  function resolveRange(mode, customStart, customEnd) {
    const now = new Date();
    let start = null, end = now;
    if (mode === 'custom') {
      if (customStart) start = new Date(customStart + 'T00:00:00');
      end = customEnd ? new Date(customEnd + 'T23:59:59.999') : now;
    } else if (mode !== 'all') {
      const days = parseInt(mode, 10) || 30;
      start = new Date(now.getTime() - days * 86400000);
    }
    return { start: start, end: end, isAll: mode === 'all' };
  }

  function currentRange() { return resolveRange(state.rangeMode, state.customStart, state.customEnd); }

  function filesInRange(range) {
    return CAD.storage.getFileList().filter(function (f) {
      const t = new Date(f.createdAt).getTime();
      if (isNaN(t)) return false;
      if (range.start && t < range.start.getTime()) return false;
      return t <= range.end.getTime();
    });
  }

  function previousRange(range) {
    if (range.isAll || !range.start) return null;
    const len = range.end.getTime() - range.start.getTime();
    const prevEnd = new Date(range.start.getTime() - 1);
    const prevStart = new Date(range.start.getTime() - len);
    return { start: prevStart, end: prevEnd };
  }

  CAD.ranges = {
    OPTIONS: RANGE_OPTS,
    resolve: resolveRange,
    inRange: filesInRange,
  };

  // ---------- small render helpers ----------

  function esc(s) { return CAD.util.escapeHtml(s); }
  function icon(name, size) { return CAD.util.icon(name, size); }

  function deltaHTML(cur, prev, hasPrev) {
    if (!hasPrev) return '';
    if (prev === 0 && cur === 0) return '<span class="delta flat">\u2014% vs previous period</span>';
    if (prev === 0) return '<span class="delta up">new vs previous period</span>';
    const pct = Math.round(((cur - prev) / prev) * 100);
    if (pct === 0) return '<span class="delta flat">0% vs previous period</span>';
    const cls = pct > 0 ? 'up' : 'down';
    const arrow = pct > 0 ? '\u2191' : '\u2193';
    return '<span class="delta ' + cls + '">' + arrow + Math.abs(pct) + '% vs previous period</span>';
  }

  function sublineHTML(count, totalInRange, cur, prev, hasPrev) {
    const share = totalInRange > 0 ? Math.round((count / totalInRange) * 100) : 0;
    return share + '% of all items \u00B7 ' + deltaHTML(cur, prev, hasPrev);
  }

  // ---------- skeleton ----------

  function ctaHTML() {
    return (
      '<div class="empty-cta card">' +
      '  <span class="cta-icon">' + icon('key-round', 28) + '</span>' +
      '  <h2>Connect your Claude account</h2>' +
      '  <p>Add an Anthropic API key in Settings, or put it in <span class="mono">.env</span>, to see which files have been shared with \u2014 and generated by \u2014 Claude.</p>' +
      '  <button class="btn btn-primary" id="cta-open-settings" type="button">Open Settings</button>' +
      '</div>'
    );
  }

  function bannerHTML() {
    const meta = CAD.storage.getSyncMeta();
    if (meta.lastSyncStatus !== 'error' || state.bannerDismissed) return '';
    const msg = (meta.lastError && meta.lastError.message) || 'Unknown error';
    return (
      '<div class="sync-banner" role="alert">' +
      '  <span class="sync-banner-icon">' + icon('alert-triangle') + '</span>' +
      '  <span class="sync-banner-text"><strong>Last sync failed.</strong> ' + esc(msg) + '</span>' +
      '  <button class="btn-link" id="banner-open-settings" type="button">Open Settings</button>' +
      '  <button class="icon-btn" id="banner-dismiss" type="button" aria-label="Dismiss">' + icon('x') + '</button>' +
      '</div>'
    );
  }

  function widget1Skeleton() {
    return (
      '<header class="widget-head">' +
      '  <div class="widget-title">' + icon('upload') + '<h2>Information Shared with AI</h2></div>' +
      '  <div class="widget-actions">' +
      '    <button class="icon-btn" id="w1-chart-toggle" type="button" title="Toggle by-day chart" aria-label="Toggle by-day chart" aria-pressed="false">' + icon('bar-chart') + '</button>' +
      '    <button class="icon-btn" id="w1-expand" type="button" title="Expand chart" aria-label="Expand chart">' + icon('maximize') + '</button>' +
      '  </div>' +
      '</header>' +
      '<div class="widget-metric">' +
      '  <button class="bignum" id="w1-bignum" type="button" title="View details"><span class="bignum-value" id="w1-bignum-value">0</span></button>' +
      '  <div class="metric-sub" id="w1-subline"></div>' +
      '</div>' +
      '<div class="sensitivity-slider">' +
      '  <label for="w1-slider">Sensitivity</label>' +
      '  <input type="range" id="w1-slider" min="0" max="3" step="1" value="' + state.sliderStop + '" aria-label="Sensitivity filter" list="w1-slider-stops">' +
      '  <div class="slider-labels" aria-hidden="true"><span>High</span><span>+ Medium</span><span>+ Low</span><span>All</span></div>' +
      '</div>' +
      '<div class="widget-body" id="w1-body"></div>'
    );
  }

  function widget2Skeleton() {
    return (
      '<header class="widget-head">' +
      '  <div class="widget-title">' + icon('sparkles') + '<h2>AI Activities</h2></div>' +
      '</header>' +
      '<div class="widget-metric">' +
      '  <button class="bignum" id="w2-bignum" type="button" title="View details"><span class="bignum-value" id="w2-bignum-value">0</span></button>' +
      '  <div class="metric-sub" id="w2-subline"></div>' +
      '</div>' +
      '<div class="widget-body" id="w2-body"></div>'
    );
  }

  // ---------- dynamic bodies ----------

  function updateBodies() {
    const taxonomy = CAD.storage.getClassification() || { categories: [] };
    const range = currentRange();
    const inRange = filesInRange(range);
    const prevR = previousRange(range);
    const prevFiles = prevR ? filesInRange(prevR) : [];
    const hasPrev = !!prevR;

    const uploadedInRange = inRange.filter(function (f) { return f.direction === 'uploaded'; });
    const generatedInRange = inRange.filter(function (f) { return f.direction === 'generated'; });
    const totalInRange = inRange.length;

    updateWidget1(taxonomy, range, uploadedInRange, generatedInRange, totalInRange, prevFiles, hasPrev);
    updateWidget2(taxonomy, range, generatedInRange, totalInRange, prevFiles, hasPrev);
  }

  function categoryRowsHTML(taxonomy, byCat, totalForPct) {
    const cats = CAD.charts.orderedCategories(taxonomy);
    return cats.map(function (cat) {
      const count = byCat[cat.id] || 0;
      const pct = totalForPct ? Math.round((count / totalForPct) * 100) : 0;
      return (
        '<button class="cat-row' + (count === 0 ? ' zero' : '') + '" type="button" data-cat="' + esc(cat.id) + '" title="' + esc(cat.label) + ' \u2014 view files">' +
        '  <span class="cat-icon" style="color:' + esc(cat.color) + '">' + icon(cat.icon || 'file-question') + '</span>' +
        '  <span class="cat-main">' +
        '    <span class="cat-topline"><span class="cat-label">' + esc(cat.label) + '</span><span class="cat-count">' + CAD.util.fmtNumber(count) + '</span></span>' +
        '    <span class="cat-bar-track"><span class="cat-bar" style="width:' + pct + '%;background:' + esc(cat.color) + (count === 0 ? ';min-width:0;opacity:0' : '') + '"></span></span>' +
        '  </span>' +
        '</button>'
      );
    }).join('');
  }

  function activityGridHTML(taxonomy, byCat) {
    const cats = CAD.charts.orderedCategories(taxonomy);
    const headCells = cats.map(function (c) {
      return '<th scope="col" title="' + esc(c.label) + '"><span class="cat-icon" style="color:' + esc(c.color) + '">' + icon(c.icon || 'file-question') + '</span><span class="visually-hidden">' + esc(c.label) + '</span></th>';
    }).join('');

    const generatedCells = cats.map(function (c) {
      const n = byCat[c.id] || 0;
      return '<td><button class="cell-btn' + (n === 0 ? ' zero' : '') + '" type="button" data-cat="' + esc(c.id) + '" title="' + esc(c.label) + ' \u2014 Generated by AI: ' + n + '">' + n + '</button></td>';
    }).join('');

    const naCells = cats.map(function () {
      return '<td><span class="cell-na" tabindex="0" title="Not available via the Claude API." aria-label="Not available via the Claude API.">\u2014</span></td>';
    }).join('');

    return (
      '<div class="activity-grid-wrap">' +
      '<table class="activity-grid">' +
      '  <thead><tr><th class="row-head" scope="row"><span class="visually-hidden">Activity</span></th>' + headCells + '</tr></thead>' +
      '  <tbody>' +
      '    <tr><th scope="row" class="row-head">Generated by AI</th>' + generatedCells + '</tr>' +
      '    <tr><th scope="row" class="row-head">Modified by AI</th>' + naCells + '</tr>' +
      '    <tr><th scope="row" class="row-head">Read by AI</th>' + naCells + '</tr>' +
      '  </tbody>' +
      '</table>' +
      '<p class="grid-footnote">Modified / Read by AI are uncovered at the POC stage.</p>' +
      '</div>'
    );
  }

  function updateWidget1(taxonomy, range, uploadedInRange, generatedInRange, totalInRange, prevFiles, hasPrev) {
    const numEl = document.getElementById('w1-bignum-value');
    const subEl = document.getElementById('w1-subline');
    const bodyEl = document.getElementById('w1-body');
    if (!numEl || !subEl || !bodyEl) return;

    const allowed = SLIDER_STOPS[state.sliderStop].include;
    const filtered = uploadedInRange.filter(function (f) { return allowed.indexOf(f.sensitivity) !== -1; });
    const prevUploaded = prevFiles.filter(function (f) { return f.direction === 'uploaded' && allowed.indexOf(f.sensitivity) !== -1; });

    numEl.textContent = CAD.util.fmtNumber(filtered.length);
    subEl.innerHTML = sublineHTML(filtered.length, totalInRange, filtered.length, prevUploaded.length, hasPrev);
    numEl.parentElement.setAttribute('aria-label', 'Information shared with AI: ' + filtered.length + ' files. View details.');

    if (state.w1Mode === 'chart') {
      bodyEl.innerHTML =
        '<div class="chart-wrap">' +
        (CAD.charts.available()
          ? '<canvas id="w1-canvas" aria-label="Uploaded files by day, stacked by category" role="img"></canvas>'
          : '<div class="empty-note">' + icon('info') + '<span>Chart library (Chart.js CDN) failed to load \u2014 list view still available.</span></div>') +
        '</div>';
      const canvas = document.getElementById('w1-canvas');
      if (canvas) CAD.charts.renderStackedByDay(canvas, filtered, range, taxonomy);
      return;
    }

    const byCat = {};
    filtered.forEach(function (f) { byCat[f.categoryId] = (byCat[f.categoryId] || 0) + 1; });
    bodyEl.innerHTML = categoryRowsHTML(taxonomy, byCat, filtered.length);
  }

  function updateWidget2(taxonomy, range, generatedInRange, totalInRange, prevFiles, hasPrev) {
    const numEl = document.getElementById('w2-bignum-value');
    const subEl = document.getElementById('w2-subline');
    const bodyEl = document.getElementById('w2-body');
    if (!numEl || !subEl || !bodyEl) return;

    numEl.textContent = CAD.util.fmtNumber(generatedInRange.length);
    const prevGenerated = prevFiles.filter(function (f) { return f.direction === 'generated'; });
    subEl.innerHTML = sublineHTML(generatedInRange.length, totalInRange, generatedInRange.length, prevGenerated.length, hasPrev);
    numEl.parentElement.setAttribute('aria-label', 'AI activities: ' + generatedInRange.length + ' generated files. View details.');

    const byCat = {};
    generatedInRange.forEach(function (f) { byCat[f.categoryId] = (byCat[f.categoryId] || 0) + 1; });
    bodyEl.innerHTML = activityGridHTML(taxonomy, byCat);
  }

  // ---------- drill-down launch points ----------

  function openDrilldown(preset) {
    CAD.drilldown.open(Object.assign({
      rangeMode: state.rangeMode,
      customStart: state.customStart,
      customEnd: state.customEnd,
    }, preset));
  }

  // ---------- render + wiring ----------

  async function render(root) {
    if (!(await CAD.api.hasCredential())) {
      root.innerHTML = ctaHTML();
      const btn = document.getElementById('cta-open-settings');
      if (btn) btn.addEventListener('click', function () { CAD.app.showView('settings'); });
      return;
    }

    state.bannerDismissed = false;
    root.innerHTML =
      bannerHTML() +
      '<div class="disclaimer">' + icon('info') + '<span>Classification is a filename-keyword simulation for this demo \u2014 not a real content or security scan.</span></div>' +
      '<div class="range-bar">' +
      '  <label class="range-label" for="range-select">' + icon('calendar') + ' Date range</label>' +
      '  <select id="range-select" class="select">' +
      RANGE_OPTS.map(function (o) { return '<option value="' + o.value + '"' + (o.value === state.rangeMode ? ' selected' : '') + '>' + o.label + '</option>'; }).join('') +
      '  </select>' +
      '  <span id="custom-range-inputs" class="custom-range' + (state.rangeMode === 'custom' ? '' : ' hidden') + '">' +
      '    <input type="date" id="range-start" class="input" aria-label="Range start" value="' + esc(state.customStart || '') + '">' +
      '    <span class="range-sep">to</span>' +
      '    <input type="date" id="range-end" class="input" aria-label="Range end" value="' + esc(state.customEnd || '') + '">' +
      '  </span>' +
      '</div>' +
      '<div class="widgets">' +
      '  <section class="card widget" id="widget-uploaded" aria-label="Information Shared with AI">' + widget1Skeleton() + '</section>' +
      '  <section class="card widget" id="widget-generated" aria-label="AI Activities">' + widget2Skeleton() + '</section>' +
      '</div>';

    wire(root);
    updateBodies();
  }

  function wire(root) {
    const $ = function (id) { return document.getElementById(id); };

    const rangeSelect = $('range-select');
    rangeSelect.addEventListener('change', function () {
      state.rangeMode = rangeSelect.value;
      $('custom-range-inputs').classList.toggle('hidden', state.rangeMode !== 'custom');
      updateBodies();
    });

    $('range-start').addEventListener('change', function () { state.customStart = this.value || null; updateBodies(); });
    $('range-end').addEventListener('change', function () { state.customEnd = this.value || null; updateBodies(); });

    const slider = $('w1-slider');
    slider.addEventListener('input', function () {
      state.sliderStop = parseInt(slider.value, 10);
      updateBodies();
    });

    $('w1-chart-toggle').addEventListener('click', function () {
      state.w1Mode = state.w1Mode === 'chart' ? 'list' : 'chart';
      this.setAttribute('aria-pressed', state.w1Mode === 'chart' ? 'true' : 'false');
      this.classList.toggle('active', state.w1Mode === 'chart');
      updateBodies();
    });

    $('w1-expand').addEventListener('click', function () {
      const taxonomy = CAD.storage.getClassification() || { categories: [] };
      const range = currentRange();
      const allowed = SLIDER_STOPS[state.sliderStop].include;
      const filtered = filesInRange(range).filter(function (f) {
        return f.direction === 'uploaded' && allowed.indexOf(f.sensitivity) !== -1;
      });
      const m = CAD.ui.modal({
        title: 'Information Shared with AI \u2014 by day',
        className: 'chart-modal',
        content:
          '<div class="chart-wrap large">' +
          (CAD.charts.available()
            ? '<canvas id="w1-modal-canvas" aria-label="Uploaded files by day, stacked by category" role="img"></canvas>'
            : '<div class="empty-note">' + icon('info') + '<span>Chart library (Chart.js CDN) failed to load.</span></div>') +
          '</div>',
        onClose: function () {
          const c = document.getElementById('w1-modal-canvas');
          if (c) CAD.charts.destroyOn(c);
        },
      });
      const canvas = m.el.querySelector('#w1-modal-canvas');
      if (canvas) requestAnimationFrame(function () { CAD.charts.renderStackedByDay(canvas, filtered, range, taxonomy); });
    });

    $('w1-bignum').addEventListener('click', function () {
      openDrilldown({ direction: 'uploaded', sensitivities: SLIDER_STOPS[state.sliderStop].include.slice() });
    });
    $('w2-bignum').addEventListener('click', function () {
      openDrilldown({ direction: 'generated', sensitivities: ['high', 'medium', 'low'] });
    });

    $('widget-uploaded').addEventListener('click', function (ev) {
      const row = ev.target.closest('.cat-row');
      if (!row) return;
      openDrilldown({
        direction: 'uploaded',
        categoryId: row.getAttribute('data-cat'),
        sensitivities: SLIDER_STOPS[state.sliderStop].include.slice(),
      });
    });

    $('widget-generated').addEventListener('click', function (ev) {
      const cell = ev.target.closest('.cell-btn');
      if (!cell) return;
      openDrilldown({
        direction: 'generated',
        categoryId: cell.getAttribute('data-cat'),
        sensitivities: ['high', 'medium', 'low'],
      });
    });

    const dismiss = $('banner-dismiss');
    if (dismiss) dismiss.addEventListener('click', function () {
      state.bannerDismissed = true;
      const b = document.querySelector('.sync-banner');
      if (b) b.remove();
    });
    const openSettings = $('banner-open-settings');
    if (openSettings) openSettings.addEventListener('click', function () { CAD.app.showView('settings'); });
  }

  CAD.dashboard = {
    render: render,
    updateBodies: updateBodies,
    state: state,
  };
})();
